import {
    Component,
    MutableRefObject,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import { QueryErrorResetBoundary, useQueryClient } from "@tanstack/react-query";
import { errorManager } from "./ErrorManager";
import { usePreload } from "@/navigation/PreloadProvider";
import { useLocation } from "react-router-dom";
import { CenteredErrorIcon } from "./CenteredErrorIcon";

export const ErrorBoundary = ({
    children,
    clearAllQueries,
    fallbackWrapper
}: {
    children: ReactNode;
    fallbackWrapper?: (children: ReactNode) => ReactNode;
    // clearAllQueries has been added for the top level because QueryErrorResetBoundary doesn't seem to work for errors that happen in the sidebar (eg IdentitySwitcherDropdown)
    // this is possibly a problem with ReactQuery but this workaround seems to work for now
    clearAllQueries?: true;
}) => {
    const queryClient = useQueryClient();
    const retryRef = useRef<() => void>(() => {
        void 0;
    });
    const [error, setError] = useState<Error | undefined>(undefined);

    const onError = (error: Error) => {
        setError(error);
    };

    const retry = () => {
        setError(undefined);
        if (clearAllQueries) {
            queryClient.clear();
        }
        retryRef.current();
    };

    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ClassErrorBoundary
                    fallback={
                        <Fallback
                            error={error}
                            resetQueries={reset}
                            retry={retry}
                            fallbackWrapper={fallbackWrapper}
                        />
                    }
                    onError={onError}
                    retryRef={retryRef}
                >
                    {children}
                </ClassErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );
};

const Fallback = ({
    error,
    resetQueries,
    retry,
    fallbackWrapper
}: {
    error: Error | undefined;
    resetQueries: () => void;
    retry: () => void;
    fallbackWrapper?: (children: ReactNode) => ReactNode;
}) => {
    const { isPreloading } = usePreload();

    const onRetry = useCallback(() => {
        resetQueries();
        retry();
    }, [resetQueries, retry]);

    // report to global error manager
    useEffect(() => {
        if (isPreloading) {
            return;
        }
        if (error) {
            errorManager.addError(error);
            return errorManager.addRetryCallback(onRetry);
        }
    }, [error, onRetry, isPreloading]);

    // auto retry error if page url changes
    // this is here as well as global error display, because global error may be intentionally hidden by the user, and then further navigation may happen
    const { pathname } = useLocation();
    const pathRef = useRef(pathname);
    useEffect(() => {
        if (isPreloading) {
            return;
        }
        if (pathname !== pathRef.current) {
            onRetry();
        }
    }, [isPreloading, pathname, onRetry]);

    const content = <CenteredErrorIcon />;

    return fallbackWrapper ? fallbackWrapper(content) : content;
};

interface IClassErrorBoundaryProps {
    fallback: ReactNode;
    children: ReactNode;
    onError: (error: Error) => void;
    retryRef: MutableRefObject<() => void>;
}

class ClassErrorBoundary extends Component<
    IClassErrorBoundaryProps,
    { hasError: boolean }
> {
    constructor(props: IClassErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
        this.props.retryRef.current = () => {
            this.setState({ hasError: false });
        };
    }

    static getDerivedStateFromError(_: Error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    override componentDidCatch(error: Error) {
        this.props.onError(error);
    }

    override render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }

        return this.props.children;
    }
}
